import React, { useState } from 'react';
import { graphql /* , Link */ } from 'gatsby';
import parse from 'html-react-parser';
import Image from '@ecs/tng-common/components/image/Image';
import { sharedAssetsPath } from '@ecs/tng-common/scripts/data';
import { imageWithPrefix } from '@ecs/tng-common/scripts/helpers';
import Footer from '@ecs/tng-common/components/footer/Footer';
import { IFontIconCard } from '@ecs/tng-common/interfaces';
import Breadcrumbs from '../../components/posts/Breadcrumbs';
import StickyBannerDynamic from '../../components/StickyBannerDynamic';
import SocialShare from '../../components/SocialShare';
import ByLine from '../../components/ByLine';
import SEO from '../../components/SEO';
import BottomPromoBanner from '../../components/posts/BottomPromoBanner';
import LayoutArticles from '../../components/LayoutArticles';
import FooterDisclosures from '../../components/ FooterDisclosures';
import {
  getCustomFieldValue,
  setSeoMetaTags,
  contWidthClases,
  processContentWithTokens
} from '../../helpers/functions';
import { addAlternateHreflang, setDataSection, setEcsSiteSection } from '../../helpers/analytics';
import ROUTES, { pathPrefix } from '../../helpers/routes';
import {
  IAuthor,
  IPost,
  ExpCustomFields,
  QSWidgetTypes,
  CalculatorTypes,
  SGWidgetTypes,
  QuidgetTypes
} from '../../helpers/interfaces';
import { useInfogramScript, useQSScript } from '../../helpers/embed-script';
import {
  ifAuthorHasProfileImage,
  returnAuthorInitials,
  restrictedAuthors
} from '../../helpers/author-profile-images';
import PostContent from '../../components/posts/PostContent';
import TableOfContents, { formTOCLinks } from '../../components/posts/TableOfContents';
import BlogCategoryLinks from '../../components/BlogCategoryLinks';
import TopAdvertiserDisclosure from '../../components/posts/TopAdvertiserDisclosure';
import { exploreMoreLinks, heroSections } from '../../content/blog-category-links';
import {
  checkIfToIncludeBoostDisclosure,
  checkIfToIncludeVantageDisclosure
} from '../../helpers/single-post-replacements';
import placeholderFeatImg from '../../../static/graphics/social-experian-generic.svg';
import { methodology } from '../../content/documents/disclosures';
import BoostCtaModal from '../../components/BoostCtaModal';

// styles
import '../../styles/post-default-template.scss';

interface IDefaultTemplateProps {
  data: {
    post: IPost;
    looker: {
      nodes: {
        token: string;
        value: string;
      }[];
    } | null;
    author?: IAuthor; // optional, doesn't come through in preview query
    editor?: IAuthor | null; // optional, doesn't come through in preview query
  };
  // eslint-disable-next-line react/no-unused-prop-types
  pageContext?: {
    // optional, doesn't come through in preview query
    postId: string;
    authId: string;
    editorName: string | null;
    lookerTokens: string[];
    hasToken: boolean;
  };
}
export default function DefaultTemplate({ data }: IDefaultTemplateProps) {
  const [toShowModal, setToShowModal] = useState(false);

  const postData = data.post;
  const lookerData = data.looker?.nodes || [];
  const expCustomFields = postData?.expCustomFields || [];
  const { categories, title, expPostContent, databaseId, content } = postData;
  const { databaseId: categoryId, slug: catSlug } = categories?.nodes?.[0] ?? {};
  const postContent = `${content}${catSlug === 'research' ? methodology : ''}`;
  const ifInfogramExists = postContent.includes('infogram-embed');
  let qa = getCustomFieldValue(ExpCustomFields.QA, expCustomFields) || '';
  if (lookerData) {
    qa = processContentWithTokens(qa, lookerData);
  }
  const toShowImgheader = () => {
    const customvalue = getCustomFieldValue(ExpCustomFields.ImgHeader, expCustomFields);
    return customvalue !== 'false';
  };
  const ctaCustom = getCustomFieldValue(ExpCustomFields.CTACustom, expCustomFields);
  const calculator = getCustomFieldValue(
    ExpCustomFields.Calculator,
    expCustomFields
  ) as CalculatorTypes | null;
  const qsWidget: QSWidgetTypes = getCustomFieldValue(
    ExpCustomFields.CtaQuinn,
    expCustomFields
  ) as QSWidgetTypes;
  const SGWidget: SGWidgetTypes = getCustomFieldValue(
    ExpCustomFields.CtaSavings,
    expCustomFields
  ) as SGWidgetTypes;
  const Quidget: QuidgetTypes = getCustomFieldValue(
    ExpCustomFields.CtaQuidget,
    expCustomFields
  ) as QuidgetTypes;
  const dateHide: string | null = getCustomFieldValue(ExpCustomFields.DateHide, expCustomFields);

  const TableOfContentsConent = formTOCLinks(postContent);
  const blogCategoryLinks: IFontIconCard[] =
    exploreMoreLinks[catSlug || ''] || heroSections[catSlug || ''] || [];
  // Use the custom hook to load scripts
  useInfogramScript(ifInfogramExists);
  useQSScript(qsWidget);
  const toggleModal = () => {
    setToShowModal((prevValue) => !prevValue);
  };
  return (
    <LayoutArticles>
      <main id="main">
        <TopAdvertiserDisclosure />
        <article className="container container-sm-py">
          <div className="row justify-content-xxl-center">
            <div className="col-12 col-xxl-10">
              <div
                className={`row ${Object.keys(TableOfContentsConent).length > 0 ? 'd-lg-block clearfix gx-lg-5 gx-xl-6 gx-xxl-7' : 'justify-content-center'} `}>
                <div className={`${contWidthClases} order-1`}>
                  <Breadcrumbs content={categories.nodes[0]} />
                  <h1>{parse(title)}</h1>
                  <ByLine
                    className="content-mb"
                    author={data.author}
                    editor={data?.editor || undefined}
                    date={dateHide === 'true' ? '' : postData.date}
                    readTime={postData.readTime}
                    databaseId={postData.databaseId}
                    isAuthorLink
                    authorImgLazyLoading={false}
                  />
                </div>
                <TableOfContents
                  content={TableOfContentsConent}
                  className="float-lg-end order-3 order-lg-2"
                />
                <div className={`${contWidthClases} order-2 order-lg-3`}>
                  {qa && (
                    <div className="rounded-3 d-flex align-items-start p-3 p-md-4 bg-light mb-6 mb-lg-7">
                      <div className="bg-teal rounded-circle p-2 me-3 flex-shrink-0">
                        <Image
                          src={`${sharedAssetsPath}/icons/icon-light-bulb.svg`}
                          alt="Light bulb icon."
                          width="40"
                          height="40"
                          className="spot-icon-sm"
                        />
                      </div>
                      <div className="qa-body">
                        <h2 className="eyebrow-text text-secondary">Quick Answer</h2>
                        {parse(qa)}
                      </div>
                    </div>
                  )}
                  {/* FEAT IMG */}
                  {toShowImgheader() ? (
                    <img
                      className={`rounded mb-4 feature-img feature-img-${expPostContent?.featImg?.position || 'middle'}`}
                      src={
                        expPostContent?.featImg?.srcWebP ||
                        expPostContent?.featImg?.src ||
                        placeholderFeatImg
                      } // fallback
                      alt={expPostContent?.featImg?.alt || 'Article image.'}
                      {...(expPostContent?.featImg?.sizes && {
                        sizes: expPostContent?.featImg?.sizes
                      })}
                      {...(expPostContent?.featImg?.srcsetWebP && {
                        srcSet:
                          expPostContent?.featImg?.srcsetWebP || expPostContent?.featImg?.srcset
                      })}
                    />
                  ) : null}
                </div>
                <div className={`${contWidthClases} order-4`}>
                  <PostContent
                    content={postContent}
                    params={{
                      postId: databaseId,
                      categoryId: categoryId,
                      catSlug: catSlug,
                      qsWidget: qsWidget,
                      sgWidget: SGWidget,
                      quidget: Quidget,
                      isSEMtemplate: false,
                      calculator: calculator,
                      isFullCalculator: false,
                      lookerData
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="col-12 col-xxl-10 mt-4 mt-lg-5">
              <div
                className={`row ${Object.keys(TableOfContentsConent).length > 0 ? 'd-lg-block clearfix gx-lg-5 gx-xl-6 gx-xxl-7' : 'justify-content-center'} `}>
                <div className={`${contWidthClases}`}>
                  <BottomPromoBanner
                    category={catSlug || ''}
                    ctaCustom={ctaCustom || ''}
                    categoryId={categoryId || ''}
                    buttonCTAclick={() => {
                      toggleModal();
                    }}
                  />
                  <div className="border-top content-mt">
                    {!restrictedAuthors.includes(data?.author?.slug || '') &&
                      data?.author?.slug &&
                      data?.author?.firstName &&
                      data?.author?.lastName &&
                      data?.author?.uri && (
                        <section className="mt-4">
                          <h2 className="eyebrow-text text-gray mb-4">About the author</h2>
                          <div className="d-flex align-items-center mb-4 byline">
                            {ifAuthorHasProfileImage(data.author.slug || '') ? (
                              <Image
                                className="rounded-circle me-2"
                                alt={`${data.author.firstName} ${data.author.lastName}`}
                                src={imageWithPrefix(`/profiles-byline/${data.author.slug}.webp`)}
                                width="32"
                                height="32"
                                lazyLoading
                              />
                            ) : (
                              <div className="author-initials me-2 text-white text-opacity-70">
                                <p className="mb-0">
                                  {returnAuthorInitials(
                                    `${data.author.firstName} ${data.author.lastName}`
                                  )}
                                </p>
                              </div>
                            )}
                            <p className="h7 mb-0">{`${data.author.firstName} ${data.author.lastName}`}</p>
                          </div>
                          {data.author?.expSeo?.description && (
                            <p className="body-text-sm text-gray">
                              {parse(data.author?.expSeo?.description)}
                            </p>
                          )}

                          <a
                            className="h7 mb-0 text-decoration-none d-flex align-items-center"
                            href={ROUTES.AUTH.toUrl(data.author.uri)}>
                            {`Read more from ${data.author.firstName}${' '}`}
                            <span className="ecs-font-icon-nav-angle-right font-icon-size-md" />
                          </a>
                        </section>
                      )}
                    {blogCategoryLinks?.length > 0 ? (
                      <section>
                        <BlogCategoryLinks links={blogCategoryLinks} />
                      </section>
                    ) : null}
                    <section>
                      <h2 className="eyebrow-text">Share article</h2>
                      <SocialShare
                        title={title}
                        link={`${pathPrefix}${postData?.uri}`} // This used canonical but no longer set by SEO
                      />
                    </section>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
        <StickyBannerDynamic
          buttonCTAclick={() => {
            toggleModal();
          }}
          category={catSlug || ''}
          ctaCustom={ctaCustom || ''}
          categoryId={categoryId || ''}
          variant="light"
        />
        <BoostCtaModal
          show={toShowModal}
          onHide={toggleModal}
        />
      </main>
      <Footer
        pageType="blog"
        disclosures={
          <FooterDisclosures
            boostDisclosure={checkIfToIncludeBoostDisclosure(content || '')}
            vantageScoreDisclosure={
              checkIfToIncludeVantageDisclosure(content || '') || calculator === 'reverse-car'
            }
          />
        }
      />
    </LayoutArticles>
  );
}

export function Head({ data, pageContext }: IDefaultTemplateProps) {
  const { categories, expCustomFields, expSeo, uri } = data.post;
  const catSlug = categories?.nodes[0]?.slug || '';
  const catId = categories?.nodes[0]?.databaseId || '';
  const title = expSeo?.title || '';
  const description = expSeo?.description || '';
  const schema = expSeo?.schema || '';
  const schemaScript = schema && <script type="application/ld+json">{schema}</script>;
  const section = setDataSection(catSlug);
  const category = catSlug;
  const quinn = getCustomFieldValue(ExpCustomFields.CtaQuinn, expCustomFields || []) || '';
  const savings = getCustomFieldValue(ExpCustomFields.CtaSavings, expCustomFields || []) || '';
  const quidget = getCustomFieldValue(ExpCustomFields.CtaQuidget, expCustomFields || []) || '';
  const authName = data.author?.slug || '';
  const hrefLang = getCustomFieldValue(ExpCustomFields.HrefLanf, expCustomFields || []);
  return (
    <>
      <body
        data-op="EXP-ASK-ART"
        data-section={section}
        data-category={category}
        data-categoryid={catId.toString()}
        data-id={pageContext?.postId.toString()}
        data-ecscategory={setEcsSiteSection('single', {
          authName,
          section,
          category,
          quinn,
          savings,
          quidget
        })}
      />
      <SEO
        title={title}
        description={description}
        noindex={expSeo?.metaRobotsNoindex !== 'index'}>
        {expSeo && setSeoMetaTags(expSeo)}
        {expSeo?.og?.updated_time && (
          <meta
            property="og:updated_time"
            content={expSeo?.og?.updated_time}
          />
        )}
        {schemaScript}
        {uri && hrefLang ? parse(addAlternateHreflang(hrefLang, uri)) : null}
      </SEO>
    </>
  );
}

// replace   fragment PostAuthor  as it gave me an arror for some reason, need to replace back
export const query = graphql`
  query SinglePost(
    $postId: Int!
    $authId: Int!
    $editorName: String
    $lookerTokens: [String]
    $hasToken: Boolean!
  ) {
    post: wpPost(databaseId: { eq: $postId }) {
      ...PostContent
      expSeo {
        ...ExpSEOFields
      }
    }
    author: wpUser(databaseId: { eq: $authId }) {
      ...PostAuthor
      expSeo {
        description
      }
    }
    editor: wpUser(name: { eq: $editorName }) {
      ...PostAuthor
    }
    looker: allLookerDataItem(filter: { token: { in: $lookerTokens } }) @include(if: $hasToken) {
      nodes {
        token
        value
      }
    }
  }
`;
